export interface PrivacySection {
  id: string;
  title: string;
  content: string[];
  bullets?: { primary: string; secondary: string }[];
}

export const PRIVACY_SECTIONS: PrivacySection[] = [
  {
    id: 'information-collection',
    title: '1. Information We Collect',
    content: [
      'We collect several types of information from and about users of our services, including:',
    ],
    bullets: [
      { primary: 'Personal Information', secondary: 'Your name, email address, postal address, phone number, and payment information when you register or place an order.' },
      { primary: 'Shipping Information', secondary: 'Origin and destination addresses, package details, and shipping preferences to facilitate delivery services.' },
      { primary: 'Usage Data', secondary: 'Information about how you interact with our services, including browsing patterns, features used, and time spent on various pages.' },
      { primary: 'Device Information', secondary: 'Technical data about the devices you use, including IP address, browser type, operating system, and device identifiers.' },
    ],
  },
  {
    id: 'information-use',
    title: '2. How We Use Your Information',
    content: ['We use the information we collect to:'],
    bullets: [
      { primary: 'Provide Services', secondary: 'Process and fulfill your shipping requests, manage your account, and provide customer support.' },
      { primary: 'Improve Experience', secondary: 'Enhance and personalize your user experience and develop new features based on user preferences.' },
      { primary: 'Communication', secondary: 'Send important service updates, delivery notifications, and promotional content (if you\'ve opted in).' },
      { primary: 'Security', secondary: 'Protect our services and users from fraudulent or illegal activities and maintain the security of our platform.' },
    ],
  },
  {
    id: 'information-sharing',
    title: '3. Information Sharing and Disclosure',
    content: [
      'Almaship - Imperia Tech LLC respects your privacy and is committed to protecting your personal information. We do not sell your personal data to third parties, and we share information only in the following limited circumstances:',
    ],
    bullets: [
      { primary: 'Service Providers', secondary: 'With carriers, delivery partners, and vendors who help us provide our services, always with appropriate data protection agreements in place.' },
      { primary: 'Legal Requirements', secondary: 'When required by law, regulation, legal process, or governmental request.' },
      { primary: 'Business Transfers', secondary: 'In connection with a merger, acquisition, or sale of assets, with appropriate safeguards for your data.' },
      { primary: 'With Your Consent', secondary: 'When you have explicitly agreed to the sharing of your information for specific purposes.' },
    ],
  },
  {
    id: 'data-security',
    title: '4. Data Security',
    content: [
      'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, disclosure, alteration, and destruction. Our security practices include 256-bit SSL encryption, secure protocols (HTTPS/TLS), strict access controls, and regular security audits.',
      'While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We continuously update our security measures as technology evolves.',
    ],
  },
  {
    id: 'cookies',
    title: '5. Cookies and Tracking Technologies',
    content: [
      'We use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and personalize content. These technologies help us understand how you interact with our website and improve our services.',
      'You can manage your cookie preferences through your browser settings. Please note that disabling certain cookies may impact the functionality of our website.',
    ],
    bullets: [
      { primary: 'Essential Cookies', secondary: 'Required for the basic functionality of our website and cannot be disabled.' },
      { primary: 'Analytical Cookies', secondary: 'Help us understand how visitors interact with our website and improve performance.' },
      { primary: 'Functional Cookies', secondary: 'Enable enhanced functionality and personalization, such as remembering your preferences.' },
      { primary: 'Targeting Cookies', secondary: 'Used to deliver relevant advertisements and track campaign effectiveness.' },
    ],
  },
  {
    id: 'your-rights',
    title: '6. Your Rights and Choices',
    content: [
      'Depending on your location, you may have certain rights regarding your personal information:',
    ],
    bullets: [
      { primary: 'Access', secondary: 'You can request a copy of the personal information we hold about you.' },
      { primary: 'Correction', secondary: 'You can request that we correct any incomplete or inaccurate information.' },
      { primary: 'Deletion', secondary: 'In certain circumstances, you can request that we delete your personal information.' },
      { primary: 'Objection', secondary: 'You can object to our processing of your personal information in certain cases.' },
      { primary: 'Data Portability', secondary: 'You can request a transfer of your personal information to another service provider.' },
    ],
  },
  {
    id: 'retention',
    title: '7. Data Retention',
    content: [
      'We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your personal information, we will securely delete or anonymize it.',
      'For business accounts, we may retain transaction records, shipment history, and invoices for up to 7 years to comply with legal and accounting obligations.',
    ],
  },
  {
    id: 'international',
    title: '8. International Data Transfers',
    content: [
      'As a global shipping platform, we may transfer your personal information to countries outside your own. We ensure that any international transfers comply with applicable data protection laws and that appropriate safeguards are in place.',
      'For users in the European Economic Area (EEA), we rely on Standard Contractual Clauses approved by the European Commission to ensure adequate protection of your personal data when it is transferred outside the EEA.',
    ],
  },
  {
    id: 'children',
    title: '9. Children\'s Privacy',
    content: [
      'Our Services are not directed to children under the age of 13, and we do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe that your child has provided us with personal information, please contact us immediately.',
      'If we become aware that we have collected personal information from a child under 13 without verification of parental consent, we will take steps to remove that information from our servers.',
    ],
  },
  {
    id: 'changes',
    title: '10. Changes to This Privacy Policy',
    content: [
      'We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.',
      'We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information. For questions or concerns, contact us at privacy@almaship.co.',
    ],
  },
];
